import { QuotationRequest, RequirementItem } from "@/types/requirement";
import { Modal, Steps, Button, Flex, Space, Typography, App, notification, Select } from "antd";
import { FC, useState, useMemo, useRef, useEffect } from "react";
import { Supplier } from "@/types/purchase";
import ContentWrap from "./style";
import SupplierListContent from "./components/SupplierList";
import MatchProductContent from "./components/MatchProduct";
import MatchPriceContent, { MatchPriceRef } from "./components/MatchPrice";
import GeneratePurchaseContent, { ItemWithPrice } from "./components/GeneratePurchase";
import PurchasedQuotationsContent from "./components/PurchasedQuotations";
import { getSupplierName } from "@/utils/supplier";
import { useTranslation } from "react-i18next";
import type { QuotationPurchaseRound } from "@/apis/quotationRequestItem";

interface MultiPurchaseStepsProps {
  items: Array<RequirementItem>;
  open: boolean;
  onClose: (needFresh: boolean) => void;
  mrd: number | null;
};

const MultiPurchaseSteps: FC<MultiPurchaseStepsProps> = ({ items, open, onClose, mrd }) => {

  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [itemPrices, setItemPrices] = useState<Array<ItemWithPrice>>([]);
  const { modal } = App.useApp();
  const [current, setCurrent] = useState<number>(0);
  const [priceRes, setPriceRes] = useState<"F" | "T">("F");
  const matchPriceRef = useRef<MatchPriceRef>(null);
  const [purchaseRound, setPurchaseRound] = useState<QuotationPurchaseRound>(0);
  const { t } = useTranslation();

  const [purchasedQuotations, setPurchasedQuotations] = useState<Array<QuotationRequest>>([]);

  useEffect(() => {
    notification.info({
      message: t("requirement.noPurchaseDataTip"), placement: "top"
    })
  }, [])

  const { disNext, disPrev } = useMemo(() => {
    let disNext = false;
    let disPrev = false;
    if (current === 0 && !!!supplier) disNext = true;
    if (current === 1 && priceRes === "F") disNext = true;
    if (current > 1) disPrev = true

    return {
      disNext,
      disPrev
    }
  }, [current, supplier, priceRes]);

  const toGenerate = () => {
    modal.confirm({
      title: t("requirement.tip"),
      content: t("requirement.enterGenerateTip"),
      onOk() {
        const prices = matchPriceRef.current?.getItemPrices();
        setItemPrices(prices || []);
        setCurrent(current + 1);
      }
    })
  }
  const handleNext = () => {
    if (current < stepsItems.length - 1) {
      // 进入生成采购单步骤时，获取itemPrices数据
      if (current === 1) toGenerate()
      else setCurrent(current + 1);
    }
  };

  const handlePrev = () => {
    if (current > 0) {
      setCurrent(current - 1);
    }
  };

  const handleClose = () => {
    // 关闭逻辑
    if (current === 2) {
      modal.confirm({
        title: t("requirement.confirmClose"),
        content: t("requirement.closeTipContent"),
        onOk() {
          onClose(purchasedQuotations.length > 0);
        }
      })
    } else onClose(purchasedQuotations.length > 0);
  };

  const purSuccess = (quotations: Array<QuotationRequest>) => {
    setPurchasedQuotations([...quotations]);
    handleNext()
  }

  const acceptSuccess = (items: Array<QuotationRequest>) => {
    setPurchasedQuotations([...items]);
  }

  const stepsItems = useMemo(() => [
    {
      title: t("requirement.selectSupplier"),
      content: <SupplierListContent value={supplier} onChange={setSupplier} disUrl />,
    },
    {
      title: t("requirement.matchPrice"),
      content: <MatchPriceContent ref={matchPriceRef} items={items} supplierId={supplier?.id ?? ""} onMatchRes={setPriceRes} />,
    },
    {
      title: t("requirement.generatePurchase"),
      content: <GeneratePurchaseContent items={itemPrices} mrd={mrd} purchaseRound={purchaseRound} onSuccess={purSuccess} />,
    },
    {
      title: t("requirement.finished"),
      content: <PurchasedQuotationsContent items={purchasedQuotations} onAcceptSuccess={acceptSuccess} />,
    },
  ], [supplier, items, priceRes, itemPrices, purchaseRound, purchasedQuotations, acceptSuccess]);
  const footer = (
    <Flex justify="space-between">
      <Space>
        {current === 1 && (
          <Button disabled={disPrev} onClick={handlePrev}>{t("requirement.prevStep")}</Button>
        )}
        {current < 2 && (
          <Button disabled={disNext} type="primary" onClick={handleNext}>{t("requirement.nextStep")}</Button>
        )}
      </Space>
      <Space>
        <Button onClick={handleClose}>{t("requirement.close")}</Button>
        {/* <Button type="primary" onClick={handleConfirm}>确认</Button> */}
      </Space>
    </Flex>
  );

  return (
    <Modal title={t("requirement.multiPurchaseTitle")} open={open}
      onCancel={handleClose}
      centered
      width="80vw"
      footer={footer}
    >
      <ContentWrap>
        <Steps current={current} items={stepsItems} />
        <Flex gap={40} align='baseline'>
          {current !== 0 && supplier && <Typography.Title level={4} style={{ marginTop: 8 }} >{t('purchase.supplierPrint')}{getSupplierName(supplier)}</Typography.Title>}
          {current === 1 && (
            <Flex gap={8} align="center">
              <span>{t("purchase.roundMethod")}</span>
              <Select<QuotationPurchaseRound>
                value={purchaseRound}
                style={{ minWidth: 120 }}
                options={[
                  { label: t("purchase.roundNone"), value: 0 },
                  { label: t("purchase.roundDown"), value: 1 },
                  { label: t("purchase.roundUp"), value: 2 },
                  { label: t("purchase.roundNearest"), value: 3 },
                ]}
                onChange={setPurchaseRound}
              />
            </Flex>
          )}
        </Flex>
        <div className="body-content">
          {stepsItems[current].content}
        </div>
      </ContentWrap>

    </Modal>
  )
};
export default MultiPurchaseSteps;
